<template>
  <div class="card-search-term" :class="{'card-search-term--current': isCurrent }">
    <div class="card-search-term__top">
      <div class="card-search-term__text">
        <p class="card-search-term__name">
          <img v-if="st.newTop3SinceLastRun" :src="require('top3_geogrid_blue.svg')" alt="" :id="`search-term-top-3-${st.text}`">
          <img v-if="st.brandNewTop3" :src="require('top3_geogrid_green.svg')" alt="" :id="`search-term-top-3-second-${st.text}`">
          <b-popover :target="`search-term-top-3-${st.text}`" triggers="hover" placement="bottom">
            At least one GeoGrid point for this search term has reached Top 3 rank for the first time
          </b-popover>
          <b-popover :target="`search-term-top-3-second-${st.text}`" triggers="hover" placement="bottom">
            At least one GeoGrid point for this search term has reached Top 3 rank for the second time
          </b-popover>
          <span>{{ st.text }}</span>
        </p>
        <span class="card-search-term__created-at">{{ st.lastGeogrid.createdAt || 'not created yet' }}</span>
      </div>
      <div class="card-search-term__geogrid">
        <GeogridPreview :ranks="st.lastGeogrid.gridRanks" />
      </div>
    </div>
    <div class="card-search-term__bottom">
      <div class="card-search-term__metrics">
        <div v-for="({name, value, delta, deltaFormatter}, metricId) in metrics"
          class="card-search-term__metric"
          :class="{active: metricId === chartMetric}"
          :key="`metric-${metricId}`"
          @click="selectChartMetric(metricId)">
          <span>
            <span>{{ name }}:</span>
            <b>{{ value }}</b>
            <span v-if="delta"
              class="card-search-term__delta"
              :class="{ 'card-search-term__delta--positive': delta < 0 }">
              <i class="fa-solid fa-triangle" />
              <span>
                {{ deltaFormatter ? deltaFormatter(delta) : delta }}
              </span>
            </span>
          </span>
        </div>
      </div>
      <div class="card-search-term__chart">
        <SearchTermChart v-if="showChart && hasHistoricalData" :data="chartMetricData" :metric="chartMetric" />
        <span v-else-if="!hasHistoricalData">No historical data</span>
      </div>
    </div>
    <div class="card-search-term__buttons" v-if="isCurrent">
      <button
        id="dropdownRunNow"
        data-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
        :disabled="isRequesting"
        type="button"
        class="btn btn-sm"
        :class="{ 'd-none': !st.lastGeogrid.createdAt }">
        GIF
      </button>
      <button @click.stop="runNow" :disabled="isRequesting" class="btn btn-sm">
        <span class="pr-1">
          Run now
        </span>
        <i class="fa-regular fa-play" />
      </button>
      <div class='dropdown-menu p-0' aria-labelledby='dropdownRunNow'>
        <a
          :href='searchTerm.getAnimationLink'
          target='_blank'
          v-show='searchTerm.geogridsHistoryTimestamp'
          class='dropdown-item small p-x-1'>
          <i class="far fa-download mr-2" />
          GIF ({{ searchTerm.geogridsHistoryTimestamp }})
        </a>
        <a class='dropdown-item small p-x-1' @click.stop='createGif'>
          <i class="far fa-film mr-2" />Generate new GIF
        </a>
        <a class='dropdown-item small p-x-1' @click.stop='createGif(false)'>
          <i class="far fa-film mr-2" />Generate new GIF (no loop)
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { DEFAULT_ERROR_MESSAGE, NOT_ENOUGH_GEOGRID_CREDITS } from '../../../../common/constants'
import GeogridPreview from '../../components/geogrid_preview'
import SearchTermChart from './search_term_line_chart'

export default {
  name: 'St',
  components: { GeogridPreview, SearchTermChart },
  props: {
    st: Object,
    canDrawChart: {
      type: Boolean,
      required: true
    },
    current: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isRequesting: false,
      showChart: true,
      chartMetric: 'agr' // agr | atgr | solv
    }
  },
  computed: {
    ...mapState(['config', 'searchTerm', 'geogrid']),
    metrics() {
      const NA = 'N/A'
      // Yes, it is indeed fun to work with floats that are represented as strings on backend for some reason
      const formatDecimalMetric = (num) => (num ? parseFloat(num).toFixed(2) : NA)
      const formatSolv = (num) => (num ? `${Math.round(parseFloat(parseFloat(num).toFixed(2)) * 100)}%` : NA)
      const getDelta = (cur, prev) => {
        const curNumber = parseFloat(cur)
        const prevNumber = parseFloat(prev)
        if (Number.isNaN(curNumber) || Number.isNaN(prevNumber)) {
          return 0
        }

        const diff = curNumber - prevNumber
        return diff
      }

      const prevAgr = this.st.metricHistory?.agrsHistory.at(-2)
      const prevAtgr = this.st.metricHistory?.atgrsHistory.at(-2)
      const prevSolv = this.st.metricHistory?.solvsHistory.at(-2)

      return {
        agr: {
          name: 'AGR',
          value: formatDecimalMetric(this.st.lastGeogrid.agr),
          delta: getDelta(this.st.lastGeogrid.agr, prevAgr),
          deltaFormatter: (delta) => formatDecimalMetric(Math.abs(delta))
        },
        atgr: {
          name: 'ATGR',
          value: formatDecimalMetric(this.st.lastGeogrid.atgr),
          delta: getDelta(this.st.lastGeogrid.atgr, prevAtgr),
          deltaFormatter: (delta) => formatDecimalMetric(Math.abs(delta))
        },
        solv: {
          name: 'SolV',
          value: formatSolv(this.st.lastGeogrid.solv),
          // solv good/bad logic is inverted
          delta: getDelta(this.st.lastGeogrid.solv, prevSolv) * (-1),
          deltaFormatter: (delta) => formatSolv(Math.abs(delta))
        }
      }
    },
    chartMetricData() {
      return this.st.metricHistory[`${this.chartMetric}sHistory`]
    },
    isCurrent() {
      return this.searchTerm.text === this.st.text
    },
    hasHistoricalData() {
      return this.st.metricHistory?.agrsHistory && this.st.metricHistory.agrsHistory.length > 1
    }
  },
  methods: {
    createGif(loop = false) {
      this.isRequesting = true
      axios.post(
        this.searchTerm.createAnimationLink,
        { keyword: this.searchTerm.text, loop: loop }
      ).then((response) => {
        if (response.data.error === 'gif in progress') {
          toastr.error(
            `For ${this.config.attrs.businessName}: ${this.st.text}`,
            'GIF generation in progress'
          )
        } else {
          toastr.success(
            `For ${this.config.attrs.businessName}: ${this.st.text}`,
            'GIF generation started',
            { timeOut: 1000 }
          )
        }
      }).catch((_error) => {
        toastr.error(DEFAULT_ERROR_MESSAGE)
      }).finally(() => {
        this.isRequesting = false
      })
    },
    runNow() {
      this.isRequesting = true
      axios.post(
        this.searchTerm.onDemainGeogridLink,
        { keyword: this.searchTerm.text }
      ).then((response) => {
        if (response.data.started) {
          toastr.success(
            'It will take a few minutes, please check back a bit later',
            'We started preparing your report'
          )
        } else if (response.data.error === 'not_enough_credits') {
          toastr.error(NOT_ENOUGH_GEOGRID_CREDITS, 'Not enough geogrid credits')
        } else {
          toastr.error(DEFAULT_ERROR_MESSAGE)
        }
      }).catch((_error) => {
        toastr.error(DEFAULT_ERROR_MESSAGE)
      }).finally(() => {
        this.isRequesting = false
      })
    },
    selectChartMetric(metricName) {
      if (this.isCurrent) {
        this.chartMetric = metricName
      }
    }
  },
  watch: {
    st: {
      handler() {
        if (!this.canDrawChart) {
          return
        }

        this.showChart = false
        this.$nextTick(() => {
          this.showChart = true
        })
      },
      deep: true
    },
    canDrawChart: {
      handler(val) {
        this.$nextTick(() => {
          this.showChart = val
        })
      }
    }
  }
}
</script>
